import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import { Reveal, EASE } from './helpers';

const items = [
    {
        q: 'What does Handoff actually do?',
        a: "We're a managed service. Over about two weeks, a consultant runs three or four 45-minute interviews with your departing expert. We write up what they actually know as short, structured notes — the why behind their decisions, not just the what. Whoever takes over can read the notes or just ask questions of them in plain English.",
    },
    {
        q: 'How is this different from a wiki, an exit interview, or an LMS?',
        a: "Exit interviews ask how someone feels. Wikis store what's already written down. LMS systems train people on what's already standardised. Handoff captures the stuff that lives in the expert's head and never makes it onto paper — the rules of thumb, the why behind every call, the option they almost picked.",
    },
    {
        q: "What is 'dignity mode'?",
        a: "When the departure is a layoff or restructure, we change the tone of every document the person receives. No 'ending' language. No talk of performance or fit. Just their work, recognised. It's switched on by default for that type of departure.",
    },
    {
        q: 'What does the person leaving actually get?',
        a: "Five short documents, all in their own words: a profile of how they think (Self-Insight), a profile they can share with anyone (Identity), the principles they leave behind (Signature), what they're well-suited for next (Opportunity), and a retrospective of what they built (Legacy). All theirs to keep, share, or delete.",
    },
    {
        q: 'Can the new hire really just ask questions?',
        a: "Yes — in plain English. Every answer comes straight from the captured notes and is cited back to a specific note. If the answer isn't in the notes, the assistant says so. It will never guess or make something up.",
    },
    {
        q: 'Who owns the data?',
        a: 'You do. Handoff processes the data; you control it. Under our standard agreement you can export or fully delete it at any time. The person leaving also gets their own copy of their personal documents — they take them with them.',
    },
    {
        q: 'Is the data used to train AI models?',
        a: 'No. We do not train any model on your data. Our AI providers operate under zero-data-retention terms for this product. Enterprise customers can also self-host the chat assistant on request.',
    },
    {
        q: "What if the departing employee doesn't want to take part?",
        a: "Participation is voluntary. Because the framing is 'a record of your work that you keep', opt-in rates are very high in practice. If someone declines, we can run a manager-led capture with them as a reviewer instead. Their personal documents are always theirs to publish, withhold, or delete.",
    },
    {
        q: "What's your security posture?",
        a: "UK Cyber Essentials today. SOC 2 Type I targeted for Q4 2026; ISO 27001 on the 2027 roadmap. We're not HIPAA-covered and don't recommend Handoff for protected health information.",
    },
    {
        q: 'How quickly can you start, and how long does it take?',
        a: 'Most engagements are scoped on a 20-minute call and start within five working days. End-to-end is typically two weeks. Pay-per-departure tiers have no annual contract. Premium and multi-departure retainers usually close in two to three weeks including security review.',
    },
];

export const FAQ = () => {
    const [open, setOpen] = useState<number | null>(0);

    const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: items.map((it) => ({
            '@type': 'Question',
            name: it.q,
            acceptedAnswer: { '@type': 'Answer', text: it.a },
        })),
    };

    return (
        <section id="faq" className="py-32 border-t border-border/40 bg-muted/20 scroll-mt-16">
            <script
                type="application/ld+json"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
            />
            <div className="max-w-3xl mx-auto px-6">
                <Reveal className="mb-12">
                    <p className="text-[11px] uppercase tracking-[0.2em] text-primary font-semibold mb-5">Questions</p>
                    <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.025em] leading-[1.05]">
                        What buyers
                        <span className="text-muted-foreground/60"> actually ask us.</span>
                    </h2>
                </Reveal>

                <Reveal>
                    <div className="divide-y divide-border/60 rounded-2xl border border-border/60 bg-card overflow-hidden">
                        {items.map((item, i) => {
                            const isOpen = open === i;
                            const panelId = `faq-panel-${i}`;
                            const buttonId = `faq-button-${i}`;
                            return (
                                <div key={i}>
                                    <button
                                        id={buttonId}
                                        aria-expanded={isOpen}
                                        aria-controls={panelId}
                                        className="w-full text-left px-6 py-5 flex items-center justify-between gap-4 hover:bg-muted/30 transition-colors"
                                        onClick={() => setOpen(isOpen ? null : i)}
                                    >
                                        <span className="font-medium text-[15px] leading-snug">{item.q}</span>
                                        <ChevronRight className={`h-4 w-4 text-muted-foreground flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-90' : ''}`} />
                                    </button>
                                    <AnimatePresence initial={false}>
                                        {isOpen && (
                                            <motion.div
                                                id={panelId}
                                                role="region"
                                                aria-labelledby={buttonId}
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.3, ease: EASE }}
                                                className="overflow-hidden"
                                            >
                                                <div className="px-6 pb-5 text-[14px] text-muted-foreground leading-relaxed">{item.a}</div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            );
                        })}
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
